// src/pages/AdminProducts.js
import React, { useEffect, useMemo, useRef, useState } from "react";

/* ---------- Env + endpoints ---------- */
const IS_LOCAL =
  typeof window !== "undefined" &&
  /localhost|127\.0\.0\.1/i.test(window.location.hostname);

const API_BASE = IS_LOCAL
  ? "http://localhost/shop/routes"
  : "https://shop.petrahcollection.co.ke/shop/routes";

const PRODUCTS_PRIMARY  = `${API_BASE}/products_petro.php`;                // full rows (auth)
const PRODUCTS_FALLBACK = `${API_BASE}/products_petro.php?action=catalog`; // public (grouped)
const META_API          = `${API_BASE}/products-meta.php`;

/* ---------- URL helpers ---------- */
const PHP_ORIGIN = new URL(API_BASE).origin;
const APP_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : PHP_ORIGIN;

const toAbs = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/images"))              return APP_ORIGIN + u;   // React /public
  if (u.startsWith("/public/"))             return PHP_ORIGIN + u;   // Apache htdocs
  if (u.startsWith("/shop/routes/uploads")) return PHP_ORIGIN + u;   // PHP uploads
  return new URL(u, PHP_ORIGIN).href;
};

/* ---------- Fetch helpers ---------- */
async function safeJsonFetch(input, init) {
  const res = await fetch(input, { headers: { Accept: "application/json" }, ...init });
  const text = await res.text();
  let json = null;
  try {
    const start = text.search(/[[]|[{]|"ok"|"data"/);
    const body  = start >= 0 ? text.slice(start) : text;
    json = body ? JSON.parse(body) : null;
  } catch {}
  if (!res.ok) {
    const msg =
      json?.error || json?.message ||
      text.slice(0, 160).replace(/\s+/g, " ").trim() ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  // Some endpoints wrap as {ok,data}; others return array directly
  return json ?? {};
}

/** Fetch full product rows (primary → fallback) */
async function fetchProductsList() {
  try {
    const j = await safeJsonFetch(PRODUCTS_PRIMARY, { credentials: "include" });
    const list = Array.isArray(j) ? j : j?.data;
    if (Array.isArray(list)) return list;
  } catch {}
  const jf = await safeJsonFetch(PRODUCTS_FALLBACK);
  const listf = Array.isArray(jf) ? jf : jf?.data;
  return Array.isArray(listf) ? listf : [];
}

/* ---------- UI atoms ---------- */
function Chip({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[11px] leading-4 bg-white ${className}`}>
      {children}
    </span>
  );
}

/* ---------- Helpers ---------- */
const norm  = (s) => (s ?? "").toString().trim();
const lower = (s) => norm(s).toLowerCase();
const keyFor = (name, color) => `${norm(name)}__${norm(color)}`;

/* =========================================================
   Component
   ========================================================= */
export default function AdminProducts() {
  // Catalog variants (unique name+color pairs)
  const [variants, setVariants] = useState([]); // [{name, color}]
  // Meta rows from PHP (strict per colorway)
  const [metaRows, setMetaRows] = useState([]); // [{product_name, color, image, display_price, updated_at}]

  // Fast lookup: (name+color) -> meta row
  const metaIndex = useMemo(() => {
    const map = new Map();
    for (const r of metaRows) {
      const n = norm(r.product_name);
      const c = norm(r.color);
      map.set(keyFor(n, c), { ...r, product_name: n, color: c });
    }
    return map;
  }, [metaRows]);

  // UI state
  const [query, setQuery] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [selectedColor, setSelectedColor] = useState(""); // empty string IS valid key
  const [price, setPrice] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null); // current variant's meta
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");
  const searchRef = useRef(null);

  /* ---------- Load variants (unique name+color) ---------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingList(true);
      setErrorList("");
      try {
        const list = await fetchProductsList();
        const seen = new Set();
        const out = [];
        for (const p of list) {
          const name  = norm(p?.name);
          const color = norm(p?.color ?? "");
          if (!name) continue;
          const k = keyFor(name, color); // strict by color
          if (!seen.has(k)) {
            seen.add(k);
            out.push({ name, color });
          }
        }
        out.sort((a, b) => {
          const n = a.name.localeCompare(b.name);
          return n || a.color.localeCompare(b.color);
        });
        if (mounted) setVariants(out);
      } catch (e) {
        if (mounted) {
          setErrorList(e.message || "Failed to load products");
          setVariants([]);
        }
      } finally {
        if (mounted) setLoadingList(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  /* ---------- Load all meta rows ---------- */
  const refreshMetaRows = async () => {
    try {
      const j = await safeJsonFetch(META_API); // returns all rows
      const list = Array.isArray(j) ? j : j?.data;
      if (Array.isArray(list)) {
        const normalized = list.map(r => ({
          ...r,
          product_name: norm(r.product_name),
          color: norm(r.color),
        }));
        setMetaRows(normalized);
      } else {
        setMetaRows([]);
      }
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => { refreshMetaRows(); }, []);

  /* ---------- Load meta for exactly (name,color) from cache ---------- */
  useEffect(() => {
    const n = norm(selectedName);
    const c = norm(selectedColor);
    if (!n && c === "") {
      setMeta(null);
      setPrice("");
      setFile(null);
      return;
    }
    const k = keyFor(n, c);
    const m = metaIndex.get(k) || null;
    setMeta(m);
    setPrice(m?.display_price ?? "");
  }, [selectedName, selectedColor, metaIndex]);

  /* ---------- Debounced filter ---------- */
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.toLowerCase().trim()), 150);
    return () => clearTimeout(t);
  }, [query]);

  const filtered = useMemo(() => {
    if (!debounced) return variants.slice(0, 250);
    return variants
      .filter(v =>
        v.name.toLowerCase().includes(debounced) ||
        v.color.toLowerCase().includes(debounced)
      )
      .slice(0, 250);
  }, [debounced, variants]);

  /* ---------- Save meta for ONE variant (name+color) ---------- */
  const onSave = async (e) => {
    e.preventDefault();
    if (!selectedName) {
      alert("Pick a product and its color (empty color is allowed if that’s how the row is stored).");
      return;
    }
    const n = norm(selectedName);
    const c = norm(selectedColor); // may be ""

    const fd = new FormData();
    fd.append("product_name", n);
    fd.append("color", c); // strict per-color save
    if (price !== "") fd.append("display_price", price);
    if (file) fd.append("image", file);

    setSaving(true);
    try {
      await safeJsonFetch(META_API, { method: "POST", body: fd });

      // Refresh table then fetch the exact row again (avoid stale memo)
      await refreshMetaRows();
      const fresh = await safeJsonFetch(`${META_API}?name=${encodeURIComponent(n)}&color=${encodeURIComponent(c)}`);
      const exact = fresh?.data ?? fresh; // supports both {ok,data} and direct row
      setMeta(exact || null);
      setPrice(exact?.display_price ?? "");
      setFile(null);
      alert("Saved for this colorway only.");
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Keys ---------- */
  const metaRowKey = (r, idx) =>
    r.key || `${r.product_name}|${(r.color||"").trim()}|${r.updated_at||""}|${idx}`;

  /* ---------- UI ---------- */
  return (
    <div className="mx-auto max-w-7xl p-3 sm:p-4 md:p-6">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-lg sm:text-2xl font-semibold">Products Admin — per-color images</h1>
        <button
          className="text-sm px-3 py-2 border rounded md:hidden"
          onClick={() => searchRef.current?.focus()}
        >
          Search
        </button>
      </header>

      {loadingList && <div className="mt-2 text-sm text-gray-600">Loading products…</div>}
      {!!errorList && <div className="mt-2 text-sm text-red-600">{errorList}</div>}

      {/* Variant picker */}
      <section className="mt-4 rounded border p-3 sm:p-4">
        <label className="block text-sm font-medium">Search by product or color</label>
        <input
          ref={searchRef}
          className="mt-1 w-full rounded border px-3 py-2 text-[15px]"
          placeholder="e.g., Airforce 1 Low, Black Plain, Red/White"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {filtered.length > 0 && (
          <div className="mt-2 max-h-72 overflow-auto rounded border bg-white text-sm divide-y">
            {filtered.map((v, i) => {
              const isSel = selectedName === v.name && selectedColor === v.color;
              return (
                <button
                  key={`${v.name}-${v.color}-${i}`}
                  className={`w-full px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
                    isSel ? "bg-gray-100" : ""
                  }`}
                  onClick={() => {
                    setSelectedName(v.name);
                    setSelectedColor(v.color);
                    setQuery(`${v.name}${v.color ? " – " + v.color : ""}`);
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate">{v.name}</span>
                    <Chip className="text-gray-700">{v.color || "—"}</Chip>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
          <span>Total variants: {variants.length.toLocaleString()}</span>
          <span className="hidden sm:inline">Each row is a single colorway.</span>
        </div>
      </section>

      {/* Editor */}
      {selectedName && (
        <form onSubmit={onSave} className="mt-4 rounded border p-3 sm:p-4">
          <div className="mb-3 grid gap-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <div className="text-xs text-gray-600">Editing</div>
              <div className="text-lg font-medium break-words">{selectedName}</div>
              <div className="mt-1"><Chip>{selectedColor || "—"}</Chip></div>
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                Changes apply to <strong>this color only</strong>.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium">Display price (KES)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="mt-1 w-full rounded border px-3 py-2"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Leave blank for 'Contact for price'"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">New image (JPG/PNG/WEBP)</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="mt-1 w-full text-sm file:mr-3 file:rounded file:border file:px-3 file:py-1"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          {/* Preview — only when exact pair matches */}
          <div className="mt-4 flex items-center gap-3 sm:gap-4 overflow-x-auto">
            {meta?.image &&
              lower(meta?.product_name) === lower(selectedName) &&
              lower(meta?.color) === lower(selectedColor) && (
                <img
                  src={toAbs(meta.image)}
                  alt="Current"
                  className="h-24 w-24 sm:h-28 sm:w-28 border rounded object-contain shrink-0"
                  title={meta.image}
                />
              )}
            {file && (
              <img
                src={URL.createObjectURL(file)}
                alt="Preview"
                className="h-24 w-24 sm:h-28 sm:w-28 border rounded object-contain shrink-0"
              />
            )}
          </div>

          <div className="mt-4 flex gap-3 flex-wrap">
            <button
              type="submit"
              disabled={saving || !selectedName}
              className="rounded bg-black px-4 py-2 text-white disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className="rounded border px-4 py-2"
              onClick={() => { setFile(null); setPrice(meta?.display_price ?? ""); }}
            >
              Reset changes
            </button>
          </div>
        </form>
      )}

      {/* Existing meta straight from PHP (one row per colorway) */}
      <section className="mt-6 rounded border p-3 sm:p-4">
        <div className="mb-2 font-medium">Existing meta</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left">
                <th className="p-2">Product</th>
                <th className="p-2">Color</th>
                <th className="p-2">Image</th>
                <th className="p-2">Display Price</th>
                <th className="p-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {metaRows
                .slice()
                .sort((a, b) => {
                  const n = a.product_name.localeCompare(b.product_name);
                  return n || a.color.localeCompare(b.color);
                })
                .map((r, idx) => (
                  <tr key={metaRowKey(r, idx)} className="border-t hover:bg-gray-50">
                    <td className="p-2">{r.product_name}</td>
                    <td className="p-2"><Chip>{r.color || "—"}</Chip></td>
                    <td className="p-2">
                      {r.image ? (
                        <img src={toAbs(r.image)} alt="" className="h-10 w-10 object-contain" title={r.image} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-2">
                      {r.display_price != null ? `KES ${Number(r.display_price).toLocaleString()}` : (
                        <span className="text-gray-400">Contact</span>
                      )}
                    </td>
                    <td className="p-2">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              {!metaRows.length && (
                <tr>
                  <td className="p-2 text-gray-500" colSpan={5}>No rows yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
